// @flow

import * as React from 'react';
import {useContext, useState, useEffect} from 'react';
import {contextStubFunction} from 'docs/util';
import {useLocation} from 'docs/data';

export const variants = {
  enter: (direction: number) => {
    return {
      x: direction * 300,
      opacity: 0,
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => {
    return {
      zIndex: 0,
      x: direction * -300,
      opacity: 0,
    };
  },
  slideLeft: {
    x: [300, 0],
    opacity: [0, 1],
  },
  slideRight: {
    x: [-300, 0],
    opacity: [0, 1],
  },
};

export const transition = {
  x: {
    type: 'spring',
    stiffness: 150,
    damping: 30,
    duration: 0.5,
  },
  opacity: {duration: 1},
};

export const controlledContentTransition = {
  x: {
    type: 'spring',
    stiffness: 150,
    damping: 30,
    duration: 0.3,
  },
  opacity: {duration: 0.5},
};

type Direction = 0 | -1 | 1;
export type ContentAnimationContextType = {|
  animatePref: string,
  direction: Direction,
  setAnimatePref: (string) => void,
  setAnimationDirection: (prevIndex: number, selectedIndex: number) => void,
|};

const ContentAnimationContext = React.createContext<ContentAnimationContextType>(
  {
    animatePref: '',
    direction: 0,
    setAnimatePref: contextStubFunction('ContentAnimation'),
    setAnimationDirection: contextStubFunction('ContentAnimation'),
  },
);

type ContentAnimationProviderProps = {|
  children: React.Node,
|};

export const ContentAnimationProvider = ({
  children,
}: ContentAnimationProviderProps) => {
  const [direction, setDirection] = useState(-1);
  // Controls animating content between ControlledContentGroups with different prefs
  const [animatePref, setAnimatePref] = useState('');
  const {location} = useLocation();

  useEffect(() => {
    // Reset animation values anytime we change the location
    // by switching docs pages or opening / closing expandos
    setAnimatePref('');
    setDirection(0);
  }, [location]);

  const setAnimationDirection = (prevIndex: number, activeIndex: number) => {
    if (prevIndex === activeIndex) {
      setDirection(0);
    } else {
      setDirection(prevIndex > activeIndex ? -1 : 1);
    }
  };

  const context = {
    animatePref,
    direction,
    setAnimatePref,
    setAnimationDirection,
  };

  return (
    <ContentAnimationContext.Provider value={context}>
      {children}
    </ContentAnimationContext.Provider>
  );
};

export const useContentAnimation = () => {
  return useContext(ContentAnimationContext);
};
